import React from 'react';
import { ManagedUser } from '../../types/auth.types';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

// Display math only ("N more entries", "Page X / Y"). The server default page
// size is 20 but DB-configurable — if that setting changes, this display drifts.
// Prev/Next bounds do NOT drift: they come from the DRF envelope via hasNext/hasPrevious.
const PAGE_SIZE = 20;

// Role chips shown per row before collapsing the remainder into "+N more".
const ROLE_CHIP_CAP = 3;

export interface UsersTableProps {
  rows: ManagedUser[];
  count: number;
  page: number;
  isPending: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onPageChange: (page: number) => void;
  onResetView: () => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectPage: () => void;
  canAdmin: boolean;
  /**
   * Drag side of the row-to-tree assignment contract. The handler must call
   * `event.dataTransfer.setData('text/plain', user.id)` (and set
   * `effectAllowed = 'move'`); `LedgerTree.onUserDrop` reads that exact key
   * back out on drop. Nothing enforces the pairing at runtime — a mismatched
   * key fails silently, the drop just never yields a user id.
   */
  onRowDragStart?: (event: React.DragEvent, user: ManagedUser) => void;
}

const activeSquareClass = (isActive: boolean) => cn('h-2.5 w-2.5 border border-ink', isActive && 'bg-ink');

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const UsersTable: React.FC<UsersTableProps> = (props) => {
  const { rows, count, page, isPending, hasNext, hasPrevious, selectedId, selectedIds, canAdmin, onRowDragStart } =
    props;

  if (isPending) return <LedgerSkeleton lines={8} className="mt-4" />;

  if (rows.length === 0) {
    return (
      <p className="py-6 font-serif text-scale-1 italic text-ink-muted">
        Nothing on file for this view.{' '}
        <button type="button" onClick={props.onResetView} className="cursor-pointer underline hover:text-accent">
          Show all users
        </button>
      </p>
    );
  }

  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const remaining = Math.max(0, count - page * PAGE_SIZE);
  const allOnPageSelected = rows.every((user) => selectedIds.has(user.id));
  const someOnPageSelected = rows.some((user) => selectedIds.has(user.id));

  // Rows only advertise drag when the viewer can administer AND the parent wired
  // a handler; the handler owns dataTransfer, this table just forwards the event.
  const dragProps = (user: ManagedUser) =>
    canAdmin && onRowDragStart
      ? { draggable: true, onDragStart: (event: React.DragEvent) => onRowDragStart(event, user) }
      : {};

  return (
    <div>
      <table className="hidden w-full border-collapse md:table" aria-label="Users">
        <thead>
          <tr className="border-b border-ink">
            {canAdmin && (
              <th className="w-8 py-1 pr-2 text-left font-normal">
                <input
                  type="checkbox"
                  aria-label="Select all on page"
                  checked={allOnPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={props.onToggleSelectPage}
                  className="cursor-pointer accent-[var(--color-accent)]"
                />
              </th>
            )}
            <th className="cap-label py-1 text-left font-normal">Name</th>
            <th className="cap-label py-1 text-left font-normal">Roles</th>
            <th className="cap-label py-1 text-left font-normal">Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((user) => (
            <tr
              key={user.id}
              data-testid="user-row"
              onClick={() => props.onSelect(user.id)}
              aria-current={user.id === selectedId ? 'true' : undefined}
              {...dragProps(user)}
              className={cn(
                'cursor-pointer border-b border-rule-faint text-scale-1 hover:bg-accent-soft',
                user.id === selectedId && 'bg-accent-soft shadow-[inset_2px_0_0_var(--color-accent)]',
              )}
            >
              {canAdmin && (
                <td className="py-1.5 pr-2">
                  <input
                    type="checkbox"
                    aria-label={`Select ${user.fullName}`}
                    checked={selectedIds.has(user.id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => props.onToggleSelect(user.id)}
                    className="cursor-pointer accent-[var(--color-accent)]"
                  />
                </td>
              )}
              <td className="py-1.5 pr-3">
                <span className="block">
                  <span className="font-medium text-ink">{user.fullName}</span>
                  <span className="block max-w-[28ch] truncate font-mono text-scale-0 text-ink-muted">
                    {user.email}
                  </span>
                </span>
              </td>
              <td className="py-1.5 pr-3">
                {user.roles.length === 0 ? (
                  <span className="font-serif italic text-ink-muted">No roles</span>
                ) : (
                  <span className="flex flex-wrap items-baseline gap-1">
                    {user.roles.slice(0, ROLE_CHIP_CAP).map((roleSummary) => (
                      <span key={roleSummary.id} className="cap-label border border-rule-faint px-1.5">
                        {roleSummary.role} · {roleSummary.organizationName}
                      </span>
                    ))}
                    {user.roles.length > ROLE_CHIP_CAP && (
                      <span className="text-scale-0 text-ink-muted">+{user.roles.length - ROLE_CHIP_CAP} more</span>
                    )}
                  </span>
                )}
              </td>
              <td className="py-1.5 pr-3">
                <span aria-hidden className={cn('inline-block', activeSquareClass(user.isActive))} />
                <span className="sr-only">{user.isActive ? 'Active' : 'Inactive'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile: rows reflow to two-line entries (no bulk select or drag on touch) */}
      <div className="md:hidden">
        {rows.map((user) => (
          <button
            key={user.id}
            type="button"
            onClick={() => props.onSelect(user.id)}
            className="block w-full border-b border-rule-faint py-2.5 text-left"
          >
            <span className="flex items-center text-scale-1 font-semibold text-ink">
              <span aria-hidden className={cn('mr-1.5 shrink-0', activeSquareClass(user.isActive))} />
              <span className="sr-only">{user.isActive ? 'Active' : 'Inactive'}</span>
              {user.fullName}
            </span>
            <span className="mt-0.5 block font-mono text-scale-0 text-ink-muted">
              {user.email} · {pluralize(user.roles.length, 'role')}
            </span>
          </button>
        ))}
      </div>

      <div className="mt-2 flex items-baseline justify-between">
        <span className="font-serif text-scale-1 italic text-ink-muted">
          {remaining > 0 ? `${remaining} more entries — j/k to move, Enter to peek` : 'j/k to move, Enter to peek'}
        </span>
        <span className="flex items-baseline gap-3">
          <button type="button" disabled={!hasPrevious} onClick={() => props.onPageChange(page - 1)}
            className="cap-label cursor-pointer disabled:cursor-default disabled:opacity-40 hover:!text-ink">
            ← Prev
          </button>
          <span className="cap-label">Page {page} / {pageCount}</span>
          <button type="button" disabled={!hasNext} onClick={() => props.onPageChange(page + 1)}
            className="cap-label cursor-pointer disabled:cursor-default disabled:opacity-40 hover:!text-ink">
            Next →
          </button>
        </span>
      </div>
    </div>
  );
};
